import {
  CodeModule,
  DatabaseTable,
  DatabaseTableCell,
  DatabaseTableRow,
  ImageModule,
  ParsedLesson,
  MultipleChoiceModule,
  ParagraphModule,
  ValidatedInputCodeModule,
  WebviewModule,
} from '../types/parsedLesson';
import { LessonModule } from '@customtypes/parsedLesson';
import {
  calculateInteraction,
  escapeHtml,
  getFileCodeLanguage,
} from '@utils/content-utils';
import { outputIsInstanceOfDatabaseTable } from '@utils/typeGuards';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
const convert = require('xml-js');

export function parseLessons(
  lessons: any,
  isWebProject: boolean,
): Array<ParsedLesson> {
  const result = lessons
    .map((lesson: any) => {
      if (!lesson.interactiveContent && !lesson.content) {
        return lesson;
      }
      const parsedValues = convert.xml2js(
        lesson.interactiveContent ? lesson.interactiveContent : lesson.content,
        {
          compact: false,
          captureSpacesBetweenElements: true,
        },
      );
      if (Object.keys(parsedValues).length == 0) {
        return null;
      }
      if (!parsedValues.elements[1].elements[0].elements) {
        return {
          lessonModules: {
            type: 'noInteraction',
            modules: [],
          },
          id: lesson.id,
        };
      }
      const result = parsedValues.elements[1].elements[0].elements
        .reduce((prev: any, curr: any) => {
          switch (curr.name) {
            case 'p':
              return parseParagraph(prev, curr);
            case 'files':
              return parseFiles(prev, curr);
            case 'img':
              return parseImage(prev, curr);

            case 'instructions':
              return parseInstructions(prev, curr);

            case 'selection':
              return parseMultiplechoice(prev, curr);

            case 'ordering':
              return parseOrdering(prev, curr);

            case 'code':
              return parseCode(prev, curr);

            case 'database':
              return parseDatabase(prev, curr);

            case 'webview':
              return parseWebview(prev, curr);

            case 'console':
              return parseConsole(prev, curr);
            default:
              return prev;
          }
        }, [])
        .map((m: any, index: number) => {
          return { ...m, index };
        });
      return {
        lessonModules: {
          ...splitModules(result, isWebProject),
          type: getInteractionType(result, isWebProject),
        },
        title: lesson.title,
        imagesUrl: lesson.imagesUrl,
        id: lesson.id,
      };
    })
    .filter((lesson: any) => lesson !== null);
  return result;
}

function parseWebview(prev: any, curr: any) {
  let content = '';
  if (Number(curr.attributes ? curr.attributes['version'] : 1) === 2) {
    content = curr.attributes['content'];
  } else {
    const dom = curr.elements ? curr.elements[0].cdata : '';
    content = dom;
  }
  return [
    ...prev,
    {
      visibleIf:
        curr.attributes != undefined ? curr.attributes['visible-if'] : 'always',
      type: 'webview',
      content,
    },
  ];
}

function parseDatabase(prev: any, curr: any) {
  return [
    ...prev,
    {
      type: 'database',
      bootstrapScript: curr.attributes.bootstrapScript,
      tables: parseDatabaseTables(curr.elements[0].elements),
      visibleIf:
        curr.attributes != undefined ? curr.attributes['visible-if'] : 'always',
    },
  ];
}

function parseDatabaseTables(
  tables: {
    name: string;
    attributes: { name: string };
    elements: {
      name: string;
      attributes: { name: string };
      elements: {
        name: string;
        attributes: { content: string };
      }[];
    }[];
  }[],
) {
  if (!tables) {
    return [];
  }

  const result: DatabaseTable[] = tables.map((t) => {
    if (!t.elements) {
      return { name: t.attributes.name, rows: [] };
    }
    const rows: DatabaseTableRow[] = t.elements.map((r) => {
      const cells: DatabaseTableCell[] = r.elements.map((c) => {
        if (c.name === 'header') {
          return {
            cellType: 'Header',
            content: c.attributes.content,
          };
        }
        if (c.name === 'data') {
          return {
            cellType: 'Data',
            content: c.attributes.content,
          };
        }
        throw new Error(`Unknown table cell type ${c.name}`);
      });
      return { tableCells: cells };
    });

    return { name: t.attributes.name, rows };
  });

  return result;
}

function parseParagraph(prev: any, curr: any) {
  if (curr.attributes && curr.attributes.version === '2') {
    const texts =
      !!curr.elements &&
      !!curr.elements.filter((x: any) => x.name === 'content')[0]
        ? curr.elements.filter((x: any) => x.name === 'content')[0].elements
        : [];
    const descriptionContent = (texts ? texts : [])
      .map((x: any) => x.attributes)
      .reduce((prev: any, curr: any) => {
        if (curr.type === 'break') {
          return prev + '\n';
        } else {
          return prev + curr.value;
        }
      }, '');
    return [...prev, { type: 'paragraph', content: descriptionContent }];
  } else {
    let nextParagraph = {
      type: 'paragraph',
      content: curr.elements
        ? curr.elements.reduce((prev: any, curr: any) => {
            if (curr.type === 'break') {
              return prev + '\n';
            } else if (curr.name === 'strong') {
              return prev + `**${curr.elements ? curr.elements[0].text : ''}**`;
            } else if (curr.name === 'code') {
              if (
                curr.elements &&
                (curr.elements.length > 1 || !curr.elements[0].text)
              ) {
                const text = curr.elements.map((el: any) => {
                  return flattenElements(el);
                });
                return prev + `\`${text.join('')}\``;
              }
              return prev + `\`${curr.elements ? curr.elements[0].text : ''}\``;
            } else if (curr.name === 'em') {
              return prev + `_${curr.elements ? curr.elements[0].text : ''}_`;
            } else if (curr.name === 'br') {
              return prev + '\n';
            } else if (curr.name === 'a') {
              return (
                prev +
                `[${curr.elements ? curr.elements[0].text : ''}](${
                  curr.attributes.href
                })`
              );
            } else {
              return prev + curr.text;
            }
          }, '')
        : '',
      visibleIf: 'always',
    };
    nextParagraph =
      curr.attributes && curr.attributes['visible-if']
        ? { ...nextParagraph, visibleIf: curr.attributes['visible-if'] }
        : nextParagraph;
    return [...prev, nextParagraph];
  }
}

function parseFiles(prev: any, curr: any) {
  const flattenFiles = curr.elements;

  prev.push({
    type: 'files',
    files:
      flattenFiles.length > 0
        ? flattenFiles.map((file: any) => {
            const newFile = {
              isDefault: file.attributes.isDefault == 'true',
              name: file.attributes.name,
              content: null,
              resultContent: null,
            };
            if (
              file.elements.filter((x: any) => x.name === 'resultContent')
                .length > 0 &&
              file.elements.filter((x: any) => x.name === 'resultContent')[0]
                .elements
            ) {
              newFile.resultContent = file.elements
                .filter((x: any) => x.name === 'resultContent')[0]
                .elements.filter(
                  (text: any) => !!text.type || !!text.attributes.value,
                )
                .reduce((prev: any, curr: any) => {
                  if (curr.attributes.type === 'break') {
                    return prev + '\n';
                  } else {
                    return prev + curr.attributes.value;
                  }
                }, '');
            } else {
              delete (newFile as any).resultContent;
            }

            if (
              file.elements.filter((x: any) => x.name === 'content').length >
                0 &&
              file.elements.filter((x: any) => x.name === 'content')[0].elements
            ) {
              newFile.content = file.elements
                .filter((x: any) => x.name === 'content')[0]
                .elements.filter(
                  (text: any) => !!text.type || !!text.attributes.value,
                )
                .reduce((prev: any, curr: any) => {
                  if (curr.attributes.type === 'break') {
                    return prev + '\n';
                  } else {
                    return prev + curr.attributes.value;
                  }
                }, '');
            } else {
              delete (newFile as any).content;
            }
            return newFile;
          })
        : [],
  });
  return prev;
}

function parseConsole(prev: any, curr: any) {
  return [
    ...prev,
    {
      type: 'console',
      content: curr.elements[0].elements
        .map((x: any) => x.attributes)
        .reduce((prev: any, curr: any) => {
          if (curr.type === 'break') {
            return prev + '\n';
          } else {
            return prev + curr.value;
          }
        }, ''),
    },
  ];
}

function parseCode(prev: any, curr: any) {
  const parseCodeContent = (code: any) =>
    code.elements
      ? code.elements
          .map((x: any) => x.attributes)
          .reduce((prev2: any, curr2: any) => {
            switch (curr2.type) {
              case 'break':
                prev2.push({ value: '\n' });
                break;

              default:
                prev2.push(curr2);
            }
            return prev2;
          }, [])
      : [];

  const parseCodeInteractions = (code: any) =>
    code.elements
      .filter((i: any) => i.attributes.isDefault === 'true')
      .map((i: any) => {
        switch (i.attributes.type) {
          case 'orderTheLines':
            return {
              type: 'codeOrdering',
            };

          case 'spell':
            return {
              type: 'codeSpell',
              startIndex: parseInt(i.attributes.startIndex),
              endIndex: parseInt(i.attributes.endIndex),
            };

          case 'validatedInput':
            return {
              type: 'codeValidatedInput',
              startIndex: parseInt(i.attributes.startIndex),
              endIndex: parseInt(i.attributes.endIndex),
            };

          case 'fillTheGap':
            return {
              type: 'codeFillTheGap',
              options: i.elements[0].elements
                .map((x: any) => x.attributes)
                .filter((x: any) => x.correct === undefined)
                .map((x: any) => {
                  return {
                    startIndex: parseInt(x.startIndex),
                    endIndex: parseInt(x.endIndex),
                  };
                }),
              wrongOptions: i.elements[0].elements
                .filter((x: any) => x.attributes.correct === 'false')
                .filter((x: any) => x.elements[0].elements)
                .filter((x: any) => x.elements[0].elements[0])
                .filter((x: any) => x.elements[0].elements[0].attributes)
                .map((x: any) => {
                  return x.elements[0].elements[0].attributes.value;
                }),
            };

          case 'selection':
            return {
              type: 'codeSelection',
              startIndex: parseInt(i.attributes.startIndex),
              endIndex: parseInt(i.attributes.endIndex),
              options: []
                .concat(...i.elements[0].elements.map((x: any) => x.elements))
                .map((x: any) =>
                  x.elements.map((e: any) => e.attributes.value).join(''),
                ),
            };

          default:
            break;
        }
        return i;
      }, []);

  const parseCodeOuput = (code: any) => {
    if (code.elements[0].name === 'table') {
      const resultTable = parseDatabaseTables([code.elements[0]])[0];
      return resultTable;
    } else {
      return []
        .concat(...code.elements.map((x: any) => x.elements))
        .map((x: any) => x.attributes)
        .reduce((prev2, curr2) => {
          switch (curr2.type) {
            case 'break':
              return prev2 + '\n';
            default:
              return prev2 + curr2.value;
          }
          // return prev2;
        }, '');
    }
  };

  return [
    ...prev,
    {
      ...{
        type: 'codeNone',
        name: curr.attributes != undefined ? curr.attributes['name'] : '',
        codeLanguage:
          curr.attributes != undefined
            ? curr.attributes['codelang']
            : undefined,
        visibleIf:
          curr.attributes != undefined
            ? curr.attributes['visible-if']
              ? curr.attributes['visible-if']
              : 'always'
            : 'always',
      },
      ...curr.elements.reduce((prev: any, curr: any) => {
        switch (curr.name) {
          case 'content':
            prev.content = parseCodeContent(curr);
            return prev;
          case 'interactions':
            prev = Object.assign(prev, parseCodeInteractions(curr)[0]);
            return prev;
          case 'output': {
            prev.output = parseCodeOuput(curr);
            return prev;
          }
          default:
            return prev;
        }
        // return prev;
      }, {}),
    },
  ];
}

function parseOrdering(prev: any, curr: any) {
  return [
    ...prev,
    {
      type: 'ordering',
      isCode: curr.attributes != undefined && curr.attributes.iscode === 'true',
      items: curr.elements.map((x: any) => {
        return {
          value: x.elements
            ? x.elements.reduce((prev: any, curr: any) => {
                switch (curr.type) {
                  case 'Code':
                    return prev + '`' + curr.text + '`';
                  case 'Strong':
                    return prev + '**' + curr.text + '**';
                  case 'Emph':
                    return prev + '*' + curr.text + '*';
                  case 'element':
                    if (curr.name === 'em') {
                      return prev + '*' + curr.elements[0].text + '*';
                    } else if (curr.name === 'strong') {
                      return prev + '**' + curr.elements[0].text + '**';
                    } else if (curr.name === 'code') {
                      return prev + '`' + curr.elements[0].text + '`';
                    } else {
                      return prev + (curr.name === 'br' ? '\n' : '');
                    }

                  default:
                    return prev + curr.text;
                }
                // return prev;
              }, '')
            : '',
        };
      }),
    },
  ];
}

function parseMultiplechoice(prev: any, curr: any) {
  const isCode =
    curr.attributes != undefined && curr.attributes.iscode === 'true';

  return [
    ...prev,
    {
      type: 'multipleChoice',
      isCode: isCode,
      items: curr.elements.map((x: any) => {
        return {
          correct: x.attributes.correct === 'true',
          content: x.elements
            ? x.elements.reduce((prev: any, curr: any) => {
                switch (curr.type) {
                  case 'Code':
                    return prev + '`' + curr.text + '`';
                  case 'Strong':
                    return prev + '**' + curr.text + '**';
                  case 'Emph':
                    return prev + '*' + curr.text + '*';
                  case 'element':
                    if (isCode) {
                      let result = '';
                      // in case its a break we cant flatten the elements, they dont exist
                      curr.name === 'br'
                        ? (result += '\n')
                        : curr.elements.map((x: any) => {
                            result += flattenElements(x);
                          });
                      return prev + result;
                    } else {
                      if (curr.name === 'em') {
                        return prev + '*' + curr.elements[0].text + '*';
                      } else if (curr.name === 'strong') {
                        return prev + '**' + curr.elements[0].text + '**';
                      } else if (curr.name === 'code') {
                        return (
                          prev +
                          '`' +
                          curr.elements
                            .map((x: any) => {
                              return flattenElements(x);
                            })
                            .join('') +
                          '`'
                        );
                      } else {
                        return prev + (curr.name === 'br' ? '\n' : '');
                      }
                    }
                  default:
                    return prev + escapeHtml(curr.text);
                }
                // return prev;
              }, '')
            : '',
        };
      }),
    },
  ];
}

function flattenElements(elem: any) {
  return elem.elements
    ? elem.elements.reduce((prev: any, curr: any) => {
        switch (curr.type) {
          case 'text':
            return prev + curr.text;
          case 'element':
            return (
              prev +
              (curr.elements ? curr.elements.map((x: any) => x.text) : '')
            );
          default:
            return prev + curr.text;
        }
      }, '')
    : elem.text;
}

function parseInstructions(prev: any, curr: any) {
  return [
    ...prev,
    {
      type: 'instructions',
      instructionsType: curr.attributes.type,
      content: curr.elements
        .map((x: any) => x.elements[0].elements[0])
        .map((x: any) => x.attributes)
        .filter((x: any) => x.type !== 'break')
        .map((x: any) => x.value),
    },
  ];
}

function parseImage(prev: any, curr: any) {
  const imageSrc = curr.attributes.src;
  const visibleIf = curr.attributes['visible-if'] || 'always';
  return [...prev, { type: 'image', src: imageSrc, visibleIf: visibleIf }];
}

function getInteractionType(moduleList: any, isWebProject: any) {
  const isFiles =
    moduleList.filter((module: any) => {
      return module.type === 'files';
    }).length > 0;
  const isMultipleChoice =
    moduleList.filter((module: any) => {
      return module.type === 'multipleChoice';
    }).length > 0;
  const isOrdering =
    moduleList.filter((module: any) => {
      return module.type === 'ordering';
    }).length > 0;
  const isCode =
    moduleList.filter((module: any) => {
      return (
        [
          'codeOrdering',
          'codeSpell',
          'codeValidatedInput',
          'codeSelection',
          'codeFillTheGap',
        ].indexOf(module.type) > -1 &&
        (module.visibleIf == 'always' || module.visibleIf == undefined)
      );
    }).length > 0;
  const isCodeNone =
    moduleList.filter((module: any) => {
      return (
        ['codeNone'].indexOf(module.type) > -1 &&
        (module.visibleIf == 'always' || module.visibleIf == undefined)
      );
    }).length > 0;
  const isInteraction =
    isFiles || isMultipleChoice || isOrdering || isCode || isCodeNone;
  if (isFiles || isWebProject) {
    return 'webProject';
  } else if (!isInteraction) {
    return 'noInteraction';
  } else if (isMultipleChoice) {
    return 'multiplechoiceInteraction';
  } else if (isOrdering) {
    return 'orderingInteraction';
  } else if (isCode) {
    return 'codeInteraction';
  } else if (isCodeNone) {
    return 'codeInteractionNone';
  } else {
    return null;
  }
}

function splitModules(modules: any, isWebProject: any) {
  const type = getInteractionType(modules, isWebProject);

  /***
   * split a list of modules up into the following structure:
   * {
   *   preInteractionModules: [],
   *   interactionModule: {},
   *   postInteractionModules: []
   * }
   * "splitType" defines the module type we use for splitting. Modules before that go into preInteractionModules.
   * Modules after that go into postInteractionModules.
   ***/
  const splitter = (splitTypes: any, modules: any) =>
    modules.reduce(
      (prev: any, curr: any) => {
        if (splitTypes.indexOf(curr.type) > -1) {
          if (prev.interactionModule) {
            return {
              ...prev,
              postInteractionModules: [...prev.postInteractionModules, curr],
            };
          } else {
            return { ...prev, interactionModule: curr };
          }
        }
        if (prev.interactionModule === null) {
          return {
            ...prev,
            preInteractionModules: [...prev.preInteractionModules, curr],
          };
        }
        return {
          ...prev,
          postInteractionModules: [...prev.postInteractionModules, curr],
        };
      },
      {
        preInteractionModules: [],
        interactionModule: null,
        postInteractionModules: [],
      },
    );

  switch (type) {
    case 'noInteraction':
    case 'webProject':
      return { modules: modules };

    case 'multiplechoiceInteraction':
      return splitter(['multipleChoice'], modules);

    case 'orderingInteraction':
      return splitter(['ordering'], modules);

    case 'codeInteraction':
      return splitter(
        [
          'codeOrdering',
          'codeSpell',
          'codeValidatedInput',
          'codeSelection',
          'codeFillTheGap',
        ],
        modules,
      );

    case 'codeInteractionNone':
      return splitter(['codeNone'], modules);

    default:
      break;
  }
}

export function mapLesson(lesson: any, index: any): ParsedLesson {
  const mappedLesson = internalMapLesson(lesson, index);
  switch (mappedLesson.type) {
    case 'Executable':
      return mappedLesson;
    case 'Interactive': {
      const interactionModule: any = mappedLesson.interactionModule;
      delete interactionModule?.index;
      return {
        ...mappedLesson,
        preInteractionModules: mappedLesson.preInteractionModules.map(
          (m: any) => {
            delete m.index;
            return m;
          },
        ),
        interactionModule: interactionModule,
        postInteractionModules: mappedLesson.postInteractionModules.map(
          (m: any) => {
            delete m.index;
            return m;
          },
        ),
      };
    }
  }
}
export function internalMapLesson(lesson: any, index: any): ParsedLesson {
  // NOTE: executable lesson
  if (lesson.executableContent) {
    const mappedSourceTables = parseTables(
      lesson.executableContent?.sourceDatabaseTables,
    );
    const mappedSolutionTables = parseTables(
      lesson.executableContent?.solutionDatabaseTables,
    );

    return {
      id: lesson.id || null,
      type: 'Executable',
      instructions: lesson.executableContent.instructions,
      preselectedFileIndex: lesson.executableContent.preselectedFileIndex,
      hints: lesson.executableContent.hints || [],
      httpOptions: lesson.executableContent.httpOptions || null,
      files: lesson.executableContent.files.map((file: any) => {
        return {
          name: file.name,
          content: file.content,
          solvedContent: file.solvedContent || file.content || '',
          codeLanguage: file.codeLanguage,
        };
      }),
      savedFiles:
        lesson.executableContent.savedFiles?.map((file: any) => {
          return {
            name: file.name,
            content: file.content,
            codeLanguage: getFileCodeLanguage(file.name),
          };
        }) || [],
      ...(mappedSourceTables?.length > 0 && {
        sourceDatabaseTables: mappedSourceTables,
      }),
      ...(mappedSolutionTables?.length > 0 && {
        solutionDatabaseTable: mappedSolutionTables[0],
      }),
    };
  }
  // FIXME: we need to get our API response objects straightened out (executableFilesContent vs executableContent)
  else if (lesson.executableFilesContent) {
    const mappedSourceTables = parseTables(
      lesson.executableFilesContent?.sourceDatabaseTables,
    );
    const mappedSolutionTables = parseTables(
      lesson.executableFilesContent?.solutionDatabaseTables,
    );

    return {
      id: lesson.id || null,
      type: 'Executable',
      instructions: lesson.executableFilesContent.instructions,
      preselectedFileIndex: lesson.executableFilesContent.preselectedFileIndex,
      hints: lesson.executableFilesContent.hints || [],
      httpOptions: lesson.executableFilesContent.httpOptions || null,
      files: lesson.executableFilesContent.files.map((file: any) => {
        return {
          name: file.name,
          content: file.content,
          solvedContent: file.solvedContent || file.content || '',
          codeLanguage: file.codeLanguage,
        };
      }),
      savedFiles:
        lesson.executableFilesContent.savedFiles?.map((file: any) => {
          return {
            name: file.name,
            content: file.content,
            codeLanguage: getFileCodeLanguage(file.name),
          };
        }) || [],
      ...(mappedSourceTables?.length > 0 && {
        sourceDatabaseTables: mappedSourceTables,
      }),
      ...(mappedSolutionTables?.length > 0 && {
        solutionDatabaseTable: mappedSolutionTables[0],
      }),
    };
  }
  // NOTE: interactive lesson
  else if (lesson.lessonModules.modules) {
    const mappedModules: Array<LessonModule> = lesson.lessonModules.modules.map(
      (m: any) => mapModule(m, lesson),
    );
    const interactionModule = mappedModules.find(
      (m) =>
        m.type === 'codeFillTheGap' ||
        m.type === 'multipleChoice' ||
        m.type === 'codeValidatedInput',
    ) as CodeModule | ValidatedInputCodeModule | MultipleChoiceModule;
    const interactionModuleIndex = mappedModules.indexOf(interactionModule!);
    let preInteractionModules: LessonModule[] = [];
    let postInteractionModules: (
      | ParagraphModule
      | WebviewModule
      | ImageModule
    )[] = [];

    if (interactionModuleIndex > 0) {
      preInteractionModules = mappedModules.slice(0, interactionModuleIndex);
      postInteractionModules = mappedModules
        .filter(isPostInteractionModule)
        .slice(interactionModuleIndex + 1);
    } else if (interactionModuleIndex === -1) {
      preInteractionModules = mappedModules;
    } else if (interactionModuleIndex === 0) {
      postInteractionModules = mappedModules
        .filter(isPostInteractionModule)
        .slice(interactionModuleIndex + 1);
    }

    switch (lesson.lessonModules.type) {
      case 'codeInteraction':
      case 'codeInteractionNone':
      case 'multiplechoiceInteraction':
      case 'noInteraction': {
        //NOTE: no code module
        return {
          type: 'Interactive',
          id: lesson.id || null,
          index: index,
          preInteractionModules: preInteractionModules,
          interactionModule: interactionModule || null,
          postInteractionModules: postInteractionModules,
        };
      }
      default: {
        throw new Error(`Unknown lesson type: ${lesson.lessonModules.type}`);
      }
    }
  } else {
    if (lesson.lessonModules.interactionModule.type === 'codeNone') {
      const interactionModule = {
        type: 'codeNone',
        files: [
          {
            isInteractive: false,
            name: lesson.lessonModules.interactionModule.name,
            content: lesson.lessonModules.interactionModule.content
              .map((c: any) => c.value)
              .join(''),
            codeLanguage: lesson.lessonModules.interactionModule.codeLanguage,
          },
          ...lesson.lessonModules.postInteractionModules
            .filter((m: any) => m.type === 'codeNone')
            .map((m: any) => {
              return {
                isInteractive: false,
                name: m.name,
                content: m.content.map((c: any) => c.value).join(''),
                codeLanguage: m.codeLanguage,
              };
            }),
          ...lesson.lessonModules.preInteractionModules
            .filter((m: any) => m.type === 'codeNone')
            .map((m: any) => {
              return {
                isInteractive: false,
                name: m.name,
                content: m.content.map((c: any) => c.value).join(''),
                codeLanguage: m.codeLanguage,
              };
            }),
        ],
        ...(outputIsInstanceOfDatabaseTable(
          lesson.lessonModules.interactionModule,
        )
          ? {
              outputTable: lesson.lessonModules.interactionModule.output,
            }
          : {
              output: lesson.lessonModules.interactionModule.output || null,
            }),
      };
      return {
        type: 'Interactive',
        id: lesson.id || null,
        index: index,
        preInteractionModules:
          lesson.lessonModules.preInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
        interactionModule: interactionModule as CodeModule,
        postInteractionModules:
          lesson.lessonModules.postInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
      };
    }
    if (lesson.lessonModules.interactionModule.type === 'codeFillTheGap') {
      const files = [
        ...lesson.lessonModules.preInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
        {
          isInteractive: true,
          name: lesson.lessonModules.interactionModule.name,
          content: lesson.lessonModules.interactionModule.content
            .map((c: any) => c.value)
            .join(''),
          codeLanguage: lesson.lessonModules.interactionModule.codeLanguage,
        },
        ...lesson.lessonModules.postInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
      ];

      const interactionModule = {
        type: 'codeFillTheGap',
        files,
        interactionOptions: lesson.lessonModules.interactionModule.options.map(
          (option: { startIndex: number; endIndex: number }) => ({
            ...calculateInteraction(
              files.find((file) => file.isInteractive)?.content || '',
              option.startIndex,
              option.endIndex,
            ),
          }),
        ),
        wrongOptions: lesson.lessonModules.interactionModule.wrongOptions,
        ...(outputIsInstanceOfDatabaseTable(
          lesson.lessonModules.interactionModule,
        )
          ? {
              outputTable: lesson.lessonModules.interactionModule.output,
            }
          : {
              output: lesson.lessonModules.interactionModule.output || null,
            }),
      };
      return {
        type: 'Interactive',
        id: lesson.id || null,
        index: index,
        preInteractionModules:
          lesson.lessonModules.preInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
        interactionModule: interactionModule as CodeModule,
        postInteractionModules:
          lesson.lessonModules.postInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
      };
    }
    if (lesson.lessonModules.interactionModule.type === 'multipleChoice') {
      const files = [
        ...lesson.lessonModules.preInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
        ...lesson.lessonModules.postInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
      ];

      const moduleWithOutput: any | undefined = [
        ...lesson.lessonModules.preInteractionModules,
        ...lesson.lessonModules.postInteractionModules,
      ].find((m: any) => m.type === 'codeNone' && m.output);

      //NOTE: a multiple choice module does not have files per se but can include files from a pre/post interaction code module which should then be used in the runfiles call to produce output
      const interactionModule = {
        type: 'multipleChoice',
        files,
        items: lesson.lessonModules.interactionModule.items.map(
          (i: any, index: number) => {
            return { correct: i.correct, value: index, text: i.content };
          },
        ),
        isCode: lesson.lessonModules.interactionModule.isCode || false,
        ...(outputIsInstanceOfDatabaseTable(moduleWithOutput)
          ? {
              outputTable: moduleWithOutput.output,
            }
          : {
              output: moduleWithOutput?.output || null,
            }),
      };
      return {
        type: 'Interactive',
        id: lesson.id || null,
        index: index,
        preInteractionModules: mapNoCodeModule(
          lesson.lessonModules.preInteractionModules,
        ),
        interactionModule: interactionModule as MultipleChoiceModule,
        postInteractionModules:
          lesson.lessonModules.postInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
      };
    }
    if (lesson.lessonModules.interactionModule.type === 'codeValidatedInput') {
      const files = [
        ...lesson.lessonModules.preInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
        {
          isInteractive: true,
          name: lesson.lessonModules.interactionModule.name,
          content: lesson.lessonModules.interactionModule.content
            .map((c: any) => c.value)
            .join(''),
          codeLanguage: lesson.lessonModules.interactionModule.codeLanguage,
        },
        ...lesson.lessonModules.postInteractionModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
      ];
      const interactionModule = {
        type: 'codeValidatedInput',
        files,
        interactionOption: calculateInteraction(
          files.find((file) => file.isInteractive)?.content || '',
          lesson.lessonModules.interactionModule.startIndex,
          lesson.lessonModules.interactionModule.endIndex,
        ),

        ...(outputIsInstanceOfDatabaseTable(
          lesson.lessonModules.interactionModule,
        )
          ? {
              outputTable: lesson.lessonModules.interactionModule.output,
            }
          : {
              output: lesson.lessonModules.interactionModule.output || null,
            }),
      };
      return {
        type: 'Interactive',
        id: lesson.id || null,
        index: index,
        preInteractionModules:
          lesson.lessonModules.preInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
        interactionModule: interactionModule as CodeModule,
        postInteractionModules:
          lesson.lessonModules.postInteractionModules.filter(
            (m: any) => m.type !== 'codeNone',
          ),
      };
    }

    return {
      type: 'Interactive',
      id: lesson.id || null,
      index: index,
      preInteractionModules: lesson.lessonModules.preInteractionModules,
      interactionModule: lesson.lessonModules.interactionModule,
      postInteractionModules: lesson.lessonModules.postInteractionModules,
    };
  }
}

function mapModule(module: any, lesson: any): LessonModule {
  switch (module.type) {
    case 'image':
      return {
        type: 'image',
        src: module.src,
        //NOTE: make these modules always visible to phase out reveal lesson convention
        visibleIf: 'always',
      };

    case 'paragraph':
      return {
        type: 'paragraph',
        content: module.content,
        //NOTE: make these modules always visible to phase out reveal lesson convention
        visibleIf: 'always',
      };

    case 'webview':
      return {
        type: 'webview',
        content: module.content,
        //NOTE: make these modules always visible to phase out reveal lesson convention
        visibleIf: 'always',
      };

    case 'database':
      return {
        type: 'database',
        bootstrapScript: module.bootstrapScript,
        tables: module.tables,
        //NOTE: make these modules always visible to phase out reveal lesson convention
        visibleIf: 'always',
      };

    default:
      throw Error(`not reachable: ${module.type}, ${lesson.id}`);
  }
}

function mapNoCodeModule(modules: any) {
  const firstCodeNoneModule = modules.find((m: any) => m.type === 'codeNone');
  if (firstCodeNoneModule) {
    const otherCodeNoneModules = modules
      .filter((m: any) => m.type === 'codeNone')
      .slice(1);
    const interactionModule = {
      type: 'codeNone',
      files: [
        {
          isInteractive: false,
          name: firstCodeNoneModule.name,
          content: firstCodeNoneModule.content
            .map((c: any) => c.value)
            .join(''),
          codeLanguage: firstCodeNoneModule.codeLanguage,
        },
        ...otherCodeNoneModules
          .filter((m: any) => m.type === 'codeNone')
          .map((m: any) => {
            return {
              isInteractive: false,
              name: m.name,
              content: m.content.map((c: any) => c.value).join(''),
              codeLanguage: m.codeLanguage,
            };
          }),
      ],
      index: firstCodeNoneModule.index,
      ...(outputIsInstanceOfDatabaseTable(firstCodeNoneModule)
        ? {
            outputTable: firstCodeNoneModule.output,
          }
        : {
            output: firstCodeNoneModule.output || null,
          }),
    };
    const noCodeNoneModules = modules.filter((m: any) => m.type !== 'codeNone');
    return [interactionModule, ...noCodeNoneModules].sort(
      (a, b) => a.index - b.index,
    );
  } else {
    return modules;
  }
}

export function parseTables(tables: any): DatabaseTable[] {
  if (!tables) {
    return [];
  }
  const parsedSourceTables = tables
    ? JSON.parse(tables as unknown as string)
    : undefined;
  return parsedSourceTables?.Tables.map((t: any) => {
    return {
      name: t.Name,
      rows: t.Rows.map((r: any) => {
        return {
          tableCells: r.Cells.map((c: any) => {
            return { cellType: c.CellType, content: c.Content };
          }),
        };
      }),
    };
  });
}

export const isPostInteractionModule = (
  m: LessonModule,
): m is ParagraphModule | ImageModule | WebviewModule =>
  m.type === 'paragraph' || m.type === 'image' || m.type === 'webview';
